'use client';

import { useState } from 'react';
import {
  HUBSPOT_ERROR_CODES,
  GENERIC_HUBSPOT_ERROR_MESSAGE,
  type HubSpotErrorCode,
} from '@workspace/constants/contact-form.constant';
import { HubSpotSubmissionData } from '@workspace/interfaces';

interface HubSpotResponse {
  success: boolean;
  message?: string;
  error?: string;
  code?: HubSpotErrorCode;
}

export function useHubSpot() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const GENERIC_ERROR = GENERIC_HUBSPOT_ERROR_MESSAGE;

  const submitToHubSpot = async (submissionData: HubSpotSubmissionData): Promise<HubSpotResponse> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/hubspot/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          submittedAt: Date.now(),
          fields: submissionData.fields,
          context: submissionData.context,
        }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        const code = (data?.code as HubSpotErrorCode) ?? HUBSPOT_ERROR_CODES.UNKNOWN_ERROR;
        setError(GENERIC_ERROR);
        return { success: false, error: GENERIC_ERROR, code };
      }

      return { success: true, message: 'Form submitted successfully' };
    } catch {
      setError(GENERIC_ERROR);
      return {
        success: false,
        error: GENERIC_ERROR,
        code: HUBSPOT_ERROR_CODES.UNKNOWN_ERROR,
      };
    } finally {
      setLoading(false);
    }
  };

  return {
    submitToHubSpot,
    loading,
    error,
  };
}
